import { apiClient } from './client';

export interface AuthUser {
  id: number;
  email: string;
  username: string | null;
  phone: string | null;
  name: string;
  role: string;
  avatar: string | null;
  /** Null for workspace owners, parent's id for invited members. */
  parent_user_id: number | null;
  email_notifications_enabled: boolean;
  notify_email: string | null;
  display_timezone: string;
  display_currency: string;
  organization_id: number | null;
  organization_name: string | null;
  organization_slug: string | null;
  organization_kind: 'internal' | 'customer' | null;
  organization_role: 'owner' | 'admin' | 'operator' | 'viewer' | 'billing_admin' | null;
  feature_keys: string[];
}

export interface LoginResponse {
  token: string;
  token_type: 'Bearer';
  expires_at: string;
  user: AuthUser;
}

export interface LoginTotpChallenge {
  totp_required: true;
  totp_token: string;
}

export type LoginResult = LoginResponse | LoginTotpChallenge;

export async function login(
  identifier: string,
  password: string,
  organizationSlug?: string,
): Promise<LoginResult> {
  const { data } = await apiClient.post<LoginResult>('/login', {
    identifier,
    password,
    ...(organizationSlug ? { organization_slug: organizationSlug } : {}),
  });
  return data;
}

export async function internalLogin(identifier: string, password: string): Promise<LoginResult> {
  const { data } = await apiClient.post<LoginResult>('/internal/login', { identifier, password });
  return data;
}

export async function internalLoginWithTotp(payload: {
  totp_token: string;
  totp_code?: string;
  recovery_code?: string;
}): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/internal/login', payload);
  return data;
}

export async function loginWithTotp(payload: {
  totp_token: string;
  totp_code?: string;
  recovery_code?: string;
  organization_slug?: string;
}): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/login', {
    ...payload,
  });
  return data;
}

export async function logout(): Promise<void> {
  await apiClient.post('/logout');
}

export async function fetchCurrentUser(): Promise<AuthUser> {
  const { data } = await apiClient.get<AuthUser>('/user');
  return data;
}

// Unified internal sessions refresh on the server and never expose IAM tokens.
export type RefreshResponse = LoginResponse | { message: string; user: AuthUser };

export async function refreshToken(): Promise<RefreshResponse> {
  const { data } = await apiClient.post<RefreshResponse>('/refresh');
  return data;
}

export async function updateProfile(patch: {
  name?: string;
  avatar?: string | null;
  email_notifications_enabled?: boolean;
  notify_email?: string | null;
  display_timezone?: string;
  display_currency?: string;
}): Promise<AuthUser> {
  const { data } = await apiClient.patch<AuthUser>('/profile', patch);
  return data;
}

export async function deleteAccount(currentPassword: string): Promise<void> {
  await apiClient.delete('/me', { data: { current_password: currentPassword } });
}

// ===== 2FA =====
export interface TwoFAStatus {
  enabled: boolean;
  recovery_codes_remaining: number;
}

export async function get2faStatus(): Promise<TwoFAStatus> {
  const { data } = await apiClient.get<TwoFAStatus>('/me/2fa/status');
  return data;
}

export async function setup2fa(): Promise<{ secret: string; uri: string }> {
  const { data } = await apiClient.post<{ secret: string; uri: string }>('/me/2fa/setup');
  return data;
}

export async function verify2fa(code: string): Promise<{ enabled: boolean; recovery_codes: string[] }> {
  const { data } = await apiClient.post<{ enabled: boolean; recovery_codes: string[] }>(
    '/me/2fa/verify',
    { code },
  );
  return data;
}

export async function disable2fa(password: string): Promise<void> {
  await apiClient.post('/me/2fa/disable', { password });
}

export async function regenerate2faRecoveryCodes(): Promise<{ recovery_codes: string[] }> {
  const { data } = await apiClient.post<{ recovery_codes: string[] }>(
    '/me/2fa/recovery-codes/regenerate',
  );
  return data;
}

export async function changePassword(payload: {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}): Promise<{ token: string; expires_at: string }> {
  const { data } = await apiClient.post<{ token: string; expires_at: string }>(
    '/change-password',
    payload,
  );
  return data;
}
